import { Injectable } from '@angular/core';

export type ThemePref = 'system' | 'light' | 'dark';
const KEY = 'h2s_v2_theme';

/** Light / dark look for the v2 app. The choice is remembered on this device: "System" follows the phone or computer's own setting
 *  (and changes with it), "Light" and "Dark" stay put. Until someone picks, it stays light (v2 launched light-only). It only tags
 *  <body>; the colours live in theme/v2.scss (`body.v2.v2-dark`). */
@Injectable({ providedIn: 'root' })
export class V2ThemeService {
  private media: MediaQueryList | null = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

  get pref(): ThemePref {
    try { const v = localStorage.getItem(KEY); if (v === 'system' || v === 'light' || v === 'dark') return v; } catch { /* storage blocked */ }
    return 'light';
  }

  get isDark(): boolean { return this.pref === 'dark' || (this.pref === 'system' && !!this.media?.matches); }

  set(pref: ThemePref): void {
    try { localStorage.setItem(KEY, pref); } catch { /* storage blocked: still apply for this session */ }
    this.apply(pref);
  }

  /** Call once at startup (after the v2 switch is applied). */
  init(): void {
    this.apply(this.pref);
    // "System" has to follow the device when it flips between light and dark.
    this.media?.addEventListener?.('change', () => { if (this.pref === 'system') this.apply('system'); });
  }

  private apply(pref: ThemePref): void {
    const dark = pref === 'dark' || (pref === 'system' && !!this.media?.matches);
    document.body.classList.toggle('v2-dark', dark);
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  }
}
